import {
  CalendarDay,
  validationMessages,
  validationRegexes,
} from 'further-ui/utils';
import { AccountType } from 'further-types/investor';
import {
  DateColumnLabels,
  UploadIssueCode,
  UploadIssueSeverity,
  UploadMode,
} from 'further-types/investor-investment-upload';
import { parse, isValid, format, getDate, getMonth, getYear } from 'date-fns';
import { GridData, GridRow } from './gridHelpersV2';

const FIELD_VALIDATIONS = [
  {
    columnId: 'phone',
    regex: validationRegexes.phoneNumber,
    message: 'Invalid phone number',
  },
  {
    columnId: 'bank_account_no',
    regex: validationRegexes.ukBankAccountNumber,
    message: 'Invalid bank account number',
  },
  {
    columnId: 'sort_code',
    regex: validationRegexes.ukSortCode,
    message: 'Invalid sort code',
  },
  { columnId: 'iban', regex: validationRegexes.iban, message: 'Invalid IBAN' },
  {
    columnId: 'key_contact_phone_number',
    regex: validationRegexes.phoneNumber,
    message: 'Invalid phone number',
  },
  {
    columnId: 'leu_bank_account_no',
    regex: validationRegexes.ukBankAccountNumber,
    message: 'Invalid bank account number',
  },
  {
    columnId: 'leu_sort_code',
    regex: validationRegexes.ukSortCode,
    message: 'Invalid sort code',
  },
  {
    columnId: 'leu_iban',
    regex: validationRegexes.iban,
    message: 'Invalid IBAN',
  },
  {
    columnId: 'email',
    regex: validationRegexes.email,
    message: 'Invalid email address',
  },
  {
    columnId: 'first_name',
    regex: validationRegexes.userName,
    message: validationMessages.nameValidationError,
  },
  {
    columnId: 'middle_name',
    regex: validationRegexes.userName,
    message: validationMessages.nameValidationError,
  },
  {
    columnId: 'bank_account_name',
    regex: validationRegexes.userName,
    message: validationMessages.nameValidationError,
  },
  {
    columnId: 'key_contact_first_name',
    regex: validationRegexes.userName,
    message: validationMessages.nameValidationError,
  },
  {
    columnId: 'key_contact_surname',
    regex: validationRegexes.userName,
    message: validationMessages.nameValidationError,
  },
  {
    columnId: 'leu_bank_account_name',
    regex: validationRegexes.userName,
    message: validationMessages.nameValidationError,
  },
];

type GridCellValue = string | number | boolean | null | undefined | CalendarDay;
type GridDropdownsData = Record<
  string,
  Array<{ label: string; value: string }> | undefined
>;

const DATE_COLUMNS = new Set([
  'identity_date',
  'investment_date',
  'payment_date',
]);
const BOOLEAN_COLUMNS = new Set([
  'use_cash_balance',
  'is_us_person',
  'claiming_eis',
  'prev_address_a_lived_here_3_years',
  'leu_prev_address_a_lived_here_3_years',
  'run_aml',
]);
const PHONE_COLUMN_IDS = new Set(['phone', 'key_contact_phone_number']);

const createIssue = (
  rowId: number,
  columnId: string,
  message: string,
  rowFullName: string,
) => ({
  rowId,
  columnId,
  code: UploadIssueCode.InvalidFieldValue,
  message,
  rowFullName,
  severity: UploadIssueSeverity.Error,
});

const getRowMeta = (row: GridRow, index: number) => {
  const rowId = Number(row.index ?? index + 1);
  const rowFullName =
    [row.first_name, row.last_or_corporate_name].filter(Boolean).join(' ') ||
    `Row ${rowId}`;
  return { rowId, rowFullName };
};

const toBooleanValue = (value: GridCellValue) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

export const toCalendarDay = (value: GridCellValue) => {
  if (value instanceof CalendarDay) return value;
  if (!value || typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Handle DD/MM/YYYY format
  if (trimmed.includes('/')) {
    const [d, m, y] = trimmed.split('/').map(Number);
    const parsed = parse(trimmed, 'dd/MM/yyyy', new Date());

    if (
      !isValid(parsed) ||
      getDate(parsed) !== d ||
      getMonth(parsed) + 1 !== m ||
      getYear(parsed) !== y
    ) {
      return undefined;
    }
    return new CalendarDay(format(parsed, 'yyyy-MM-dd'));
  }

  // Handle ISO format (in case of CSV pasted in)
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
    ? new CalendarDay(trimmed)
    : undefined;
};

export const isValidDateValue = (value: GridCellValue) =>
  toCalendarDay(value) != null;

const isEmpty = (value: GridCellValue) =>
  value == null || value === '' || (typeof value === 'string' && !value.trim());

const toTrimmedString = (value: GridCellValue) =>
  (typeof value === 'string' ? value.trim() : '') || null;

const normalizePhoneInput = (value: string) =>
  value.replace(/[^\d ()+]/g, '').trim();

const isLegalEntityRow = (row: GridRow) => {
  if (row.account_type === AccountType.LegalEntity) {
    return true;
  }

  const firstName = toTrimmedString(row.first_name);
  const lastOrCorporateName = toTrimmedString(row.last_or_corporate_name);
  return !firstName && lastOrCorporateName !== null;
};

export const getValidationIssues = (gridData: GridData) =>
  gridData.flatMap((row, index) => {
    const { rowId, rowFullName } = getRowMeta(row, index);

    const dateIssues = [...DATE_COLUMNS]
      .filter((col) => !isEmpty(row[col]) && !isValidDateValue(row[col]))
      .map((col) =>
        createIssue(
          rowId,
          col,
          `${DateColumnLabels[col] ?? col} is invalid.`,
          rowFullName,
        ),
      );

    const fieldIssues = FIELD_VALIDATIONS.filter(({ columnId, regex }) => {
      const value = toTrimmedString(row[columnId]);
      if (value === null) {
        return false;
      }
      const valueToTest = PHONE_COLUMN_IDS.has(columnId)
        ? normalizePhoneInput(value)
        : value;
      return valueToTest !== '' && !regex.test(valueToTest);
    }).map(({ columnId, message }) =>
      createIssue(rowId, columnId, message, rowFullName),
    );

    const lastOrCorporateNameValue = toTrimmedString(
      row.last_or_corporate_name,
    );

    // Only validate last/corporate name for non-legal entity rows
    const lastOrCorporateNameIssues =
      isLegalEntityRow(row) ||
      lastOrCorporateNameValue === null ||
      validationRegexes.userName.test(lastOrCorporateNameValue)
        ? []
        : [
            createIssue(
              rowId,
              'last_or_corporate_name',
              validationMessages.nameValidationError,
              rowFullName,
            ),
          ];

    return [...dateIssues, ...fieldIssues, ...lastOrCorporateNameIssues];
  });

const normaliseRowValue = (key: string, value: GridCellValue) => {
  if (value === '' || value === null) return undefined;

  if (key === 'index' && typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  }

  if (BOOLEAN_COLUMNS.has(key)) return toBooleanValue(value);
  if (PHONE_COLUMN_IDS.has(key) && typeof value === 'string') {
    const normalized = normalizePhoneInput(value);
    return normalized || undefined;
  }
  if (DATE_COLUMNS.has(key)) {
    const asCalendarDay = toCalendarDay(value);
    if (asCalendarDay !== undefined) {
      return asCalendarDay;
    }

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }

    return undefined;
  }

  return value;
};

export const mapGridRowToV2 = (row: GridRow) =>
  Object.fromEntries(
    Object.entries(row).flatMap(([key, value]) => {
      const normalised = normaliseRowValue(key, value);
      return normalised !== undefined ? [[key, normalised]] : [];
    }),
  );

export const mapGridDataToV2 = (gridData: GridData) =>
  gridData.map(mapGridRowToV2);

const mapGridDropdownsDataToApi = (gridDropdownsData: GridDropdownsData) =>
  Object.fromEntries(
    Object.entries(gridDropdownsData).map(([key, items]) => [
      key,
      (items ?? []).map(({ value }) => ({ value })),
    ]),
  );

const buildUploadInput = (
  gridData: GridData,
  gridDropdownsData: GridDropdownsData,
  firmId?: string,
  uploadMode?: UploadMode,
) => ({
  gridData: mapGridDataToV2(gridData),
  gridDropdownsData: mapGridDropdownsDataToApi(gridDropdownsData),
  firmId,
  ...(uploadMode !== undefined && { uploadMode }),
});

export const buildValidateAndPreviewInput = buildUploadInput;
export const buildUploadSaveInput = buildUploadInput;
